import { Component } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { DialogService } from 'src/app/core/dialog.service';
import { ErrorService } from 'src/app/core/error.service';
import { FormErrorStateMatcher, FormService } from 'src/app/core/form.service';
import { ReportService } from 'src/app/core/report.service';
import { ReportType } from 'src/app/shared/constants/operationals';
import { REPORT_DOWNLOAD_SUCCESS_MESSAGE, REPORT_DOWNLOAD_SUCCESS_TITLE } from 'src/app/shared/constants/translations';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { CompaniesService } from 'src/app/core/companies.service';
import { ExcludeGdpr } from 'src/app/core/buyers.service';

const MY_DATE_FORMAT = {
  parse: {
    dateInput: 'DD/MM/YYYY', // this is how your date will be parsed from Input
  },
  display: {
    dateInput: 'DD/MM/YYYY', // this is how your date will get displayed on the Input
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-report-form',
  templateUrl: './report-form.component.html',
  styleUrls: ['./report-form.component.scss'],

  providers: [
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMAT },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
  ],
})
export class ReportFormComponent {
  report_form_group: FormGroup = this.form_service.report_form_group;
  error_matcher: FormErrorStateMatcher = this.form_service.matcher;
  is_export_running: boolean = false;
  company: ReportType = ReportType.COMPANY;
  order: ReportType = ReportType.ORDER;
  unpaid: ReportType = ReportType.UNPAID;
  store: ReportType = ReportType.STORE;
  seller: ReportType = ReportType.SELLER;
  buyer: ReportType = ReportType.BUYER;
  vop_request: ReportType = ReportType.VOP_REQUEST;
  yes: ExcludeGdpr = ExcludeGdpr.yes;
  no: ExcludeGdpr = ExcludeGdpr.no;
  company_agreement_list: string[] = [];
  is_list_running: boolean = false;

  constructor(
    private form_service: FormService,
    private reports_service: ReportService,
    private dialog_service: DialogService,
    private error_service: ErrorService,
    private companies_service: CompaniesService,
  ) { }

  ngOnInit(): void {
    this.listCompanyAgreements();
  }

  onExportClick(): void {
    if (this.isFormValid()) {
      const form_values = this.report_form_group.value;
      const filters: any = {};
      const email = form_values[this.form_service.email_label];
      const report_type = form_values[this.form_service.report_type_label];

      if (this.isOrderOrUnpaid()) {
        filters.report_from = form_values[this.form_service.from_label]
          ? new Date(form_values[this.form_service.from_label]).getTime()
          : undefined;
        filters.report_to = form_values[this.form_service.to_label]
          ? new Date(form_values[this.form_service.to_label]).getTime()
          : undefined;
      }

      if (report_type === this.company) {
        filters.status = form_values[this.form_service.status_label];
        filters.agreement = form_values[this.form_service.agreement_label];
        filters.product_category = form_values[this.form_service.product_category_label];
      }

      if (report_type === this.store) {
        filters.status = form_values[this.form_service.status_label];
        filters.created_from = form_values[this.form_service.created_from_label]
          ? new Date(form_values[this.form_service.created_from_label]).getTime()
          : undefined;
        filters.created_to = form_values[this.form_service.created_to_label]
          ? new Date(form_values[this.form_service.created_to_label]).getTime()
          : undefined;
      }

      if (report_type === this.seller) {
        filters.status = form_values[this.form_service.status_label];
        filters.created_from = form_values[this.form_service.created_from_label]
          ? new Date(form_values[this.form_service.created_from_label]).getTime()
          : undefined;
        filters.created_to = form_values[this.form_service.created_to_label]
          ? new Date(form_values[this.form_service.created_to_label]).getTime()
          : undefined;
        filters.last_login_from = form_values[this.form_service.last_login_from_label]
          ? new Date(form_values[this.form_service.last_login_from_label]).getTime()
          : undefined;
        filters.last_login_to = form_values[this.form_service.last_login_to_label]
          ? new Date(form_values[this.form_service.last_login_to_label]).getTime()
          : undefined;
      }

      if (report_type === this.order) {
        filters.amount_min = form_values[this.form_service.amount_min_label]
          ? Number(form_values[this.form_service.amount_min_label]) * 100
          : undefined;
        filters.amount_max = form_values[this.form_service.amount_max_label]
          ? Number(form_values[this.form_service.amount_max_label]) * 100
          : undefined;
      }

      if (report_type === this.buyer) {
        filters.exclude_gdpr = form_values[this.form_service.exclude_gdpr_label];
        filters.onboarding_completed_from = form_values[this.form_service.onboarding_completed_from_label]
          ? new Date(form_values[this.form_service.onboarding_completed_from_label]).getTime()
          : undefined;
        filters.onboarding_completed_to = form_values[this.form_service.onboarding_completed_to_label]
          ? new Date(form_values[this.form_service.onboarding_completed_to_label]).getTime()
          : undefined;
      }

      if (report_type === this.vop_request) {
        filters.created_from = form_values[this.form_service.created_from_label]
          ? new Date(form_values[this.form_service.created_from_label]).getTime()
          : undefined;
        filters.created_to = form_values[this.form_service.created_to_label]
          ? new Date(form_values[this.form_service.created_to_label]).getTime()
          : undefined;
      }

      this.export(email, report_type, filters);
    }
  }

  export(email: string, report_type: string, filters: any): void {
    this.is_export_running = true;

    const handle_response = {
      next: () => {
        this.dialog_service.openInfoDialog(REPORT_DOWNLOAD_SUCCESS_MESSAGE, REPORT_DOWNLOAD_SUCCESS_TITLE);
        this.is_export_running = false;
      },
      error: (error: any) => {
        this.error_service.processGenericApiError(error);
        this.is_export_running = false;
      },
    };

    this.reports_service.export(email, report_type, filters).subscribe(handle_response);
  }

  isFormValid(): boolean {
    return this.form_service.validateForm(this.report_form_group);
  }

  onReportTypeChange(): void {
    const controls = [
      this.form_service.from_label,
      this.form_service.to_label,
      this.form_service.status_label,
      this.form_service.agreement_label,
      this.form_service.product_category_label,
      this.form_service.cms_label,
      this.form_service.created_from_label,
      this.form_service.created_to_label,
      this.form_service.last_login_from_label,
      this.form_service.last_login_to_label,
      this.form_service.amount_min_label,
      this.form_service.amount_max_label,
      this.form_service.onboarding_completed_from_label,
      this.form_service.onboarding_completed_to_label,
      this.form_service.exclude_gdpr_label,
    ];

    controls.forEach(control => {
      this.report_form_group.get(control)?.setValue(null);
      this.report_form_group.get(control)?.clearValidators();
      this.report_form_group.get(control)?.updateValueAndValidity();
    });

    if (this.isOrderOrUnpaid()) {
      this.report_form_group.get(this.form_service.from_label)?.setValidators([Validators.required]);
      this.report_form_group.get(this.form_service.to_label)?.setValidators([Validators.required]);
      this.report_form_group.get(this.form_service.from_label)?.updateValueAndValidity();
      this.report_form_group.get(this.form_service.to_label)?.updateValueAndValidity();
    }
  }

  isOrderOrUnpaid(): boolean {
    return (
      this.report_form_group.get(this.form_service.report_type_label)?.value === this.unpaid ||
      this.report_form_group.get(this.form_service.report_type_label)?.value === this.order
    );
  }

  listCompanyAgreements(): void {
    this.is_list_running = true;
    this.companies_service.listCompanyAgreements().subscribe({
      next: response => {
        this.company_agreement_list = response.data.data;
        this.is_list_running = false;
      },
      error: error => {
        this.error_service.processGenericApiError(error);
        this.is_list_running = false;
      },
    });
  }
}
